import { Button, Text } from '@telegram-apps/telegram-ui';

/**
 * Blocking modal shown when the Worker rejects a request with `401 expired` —
 * i.e. the user's Telegram-issued initData has aged past the server's
 * INITDATA_MAX_AGE_SEC window.
 *
 * Telegram doesn't refresh initData while the Mini App is open; the only
 * recovery is for the user to close + reopen the Mini App from the bot menu.
 * We can't *force* a fresh launch programmatically — `miniApp.close()` shuts
 * the Mini App down, then the user must tap the menu button again.
 *
 * The modal uses inline styles + a portal-less full-viewport overlay rather
 * than the @telegram-apps/telegram-ui Modal component because:
 *   - This is a terminal state: no reason to be lightweight/dismissible
 *   - We want to fully block all interaction beneath
 *   - It's a one-off; no reusable component value
 *
 * Wired up in App.tsx via onSessionExpired() listener.
 */
export interface SessionExpiredModalProps {
  /** Optional detail from the server (e.g. "expired (age=4321s, max=3600s)") */
  detail?: string;
  /**
   * Which attempt this is in the user's session-expired streak.
   *   1 = first time the user sees this modal (since last successful /api/me)
   *   2+ = user already closed+reopened, but Telegram resumed with the same
   *        cached initData (common on Desktop). Modal switches to a stronger
   *        copy that asks them to fully quit Telegram.
   * Default 1 if not provided.
   */
  attempt?: number;
  /** Called when the user taps "Close app". Should invoke miniApp.close(). */
  onClose: () => void;
}

export function SessionExpiredModal({ detail, attempt = 1, onClose }: SessionExpiredModalProps) {
  const isRepeat = attempt >= 2;
  const title = isRepeat ? 'Still expired' : 'Session expired';
  const body = isRepeat
    ? 'Telegram is caching your old session. Please fully quit Telegram (⌘Q on Mac, swipe up on phone) and reopen — then launch the app from the bot menu.'
    : 'The app has been open for too long. Close and reopen it from the bot menu to continue. If it still doesn\'t work, fully quit Telegram and reopen.';
  const cta = isRepeat ? 'Close app & try again' : 'Close app';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--tgui--bg_color, #ffffff)',
          color: 'var(--tgui--text_color, #000000)',
          borderRadius: 20,
          padding: '28px 24px 20px',
          maxWidth: 360,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Heart-with-clock icon to convey "session timed out" without import deps */}
        <div
          style={{
            width: 56,
            height: 56,
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
          }}
        >
          ⏱️
        </div>

        <Text
          id="session-expired-title"
          weight="2"
          style={{ fontSize: 18, marginBottom: 8, display: 'block' }}
        >
          {title}
        </Text>

        <Text
          style={{
            fontSize: 14,
            color: 'var(--tgui--hint_color, #707579)',
            display: 'block',
            lineHeight: 1.4,
            marginBottom: 24,
          }}
        >
          {body}
        </Text>

        <Button
          mode="filled"
          size="m"
          stretched
          onClick={onClose}
        >
          {cta}
        </Button>

        {detail && import.meta.env.DEV && (
          <Text
            style={{
              fontSize: 11,
              color: 'var(--tgui--hint_color, #999)',
              marginTop: 12,
              fontFamily: 'monospace',
              display: 'block',
              wordBreak: 'break-all',
            }}
          >
            {detail}
          </Text>
        )}
      </div>
    </div>
  );
}
